import { selectActiveProgramPackage } from '@quickmd-llc/frontend-utils'
import { useGetPatientsPatientIdPackages } from 'api/generated/package/package'
import { type PatientPackageApiOutContract } from 'api/generated/pwrdbyQuickMDProvidersApi.schemas'
import { useSelector } from 'store/hooks/useSelector'
import { selectProgramVisitBadgeFlag } from 'store/flags/FlagsSelectors'

/**
 * The plan the patient chart header's chip speaks for, or `null` when there is
 * nothing to speak for — because the patient holds no usable plan, or the kill
 * switch is off.
 *
 * Reads `GET /api/v1/patients/{patientId}/packages` rather than deriving from
 * the chart's visit lists, which are the only other source of coverage data and
 * are fetched lazily when a clinician expands a section: a header chip built on
 * those would be silently absent until someone clicked. The purchase is also the
 * only place the plan's own lifecycle lives — an expired plan is
 * indistinguishable from a live one on a visit's `Coverage` block.
 *
 * The flag gates the request itself, not just the chip, so a chart loaded with
 * the switch off makes no call at all. The patient id gates it for the same
 * reason: the chart renders its header before the patient resolves, and an
 * ungated call would spend that first render requesting `/patients//packages`.
 *
 * Shares `program_visit_badge_provider` with the visit chips rather than adding
 * a second key — one switch turns the whole plan surface off in this app.
 *
 * "Which plan is the current one" is the same question in every app, so the
 * answer lives in `@quickmd-llc/frontend-utils`. It is generic over the package
 * type rather than naming one: each repo generates its own non-assignable
 * contract from its own swagger. The generic hands this app back its own
 * `PatientPackageApiOutContract`.
 */
export function useProgramPlanPackage(patientId: string | null | undefined): PatientPackageApiOutContract | null {
  const isProgramBadgeEnabled = useSelector(selectProgramVisitBadgeFlag)
  const { data: packages } = useGetPatientsPatientIdPackages(patientId ?? '', {
    query: {
      // `Boolean(...)` rather than a bare `&&`: the flag selector returns
      // `boolean | undefined` while the flags slice is still empty, and provider
      // runs `exactOptionalPropertyTypes`, which rejects an explicit `undefined`.
      enabled: Boolean(isProgramBadgeEnabled) && Boolean(patientId),
    },
  })

  if (!isProgramBadgeEnabled) {
    return null
  }

  return selectActiveProgramPackage(packages)
}
